import {
  getApiErrorCode,
  getApiErrorMessage,
  isRecord,
  parseApiResponse,
} from '@/shared/utils/apiResponse';
import { request } from '@/shared/utils/requestBuilder';

import type {
  AcessoAoArquivo,
  AcessoDaSimulacao,
  AssinaturaSimulada,
  AvancoDoRelogio,
  ConfiguracaoDoAdmin,
  DadosDeInicio,
  DadosDeInicioDoAdmin,
  DadosDoAnexo,
  ErroDaSimulacao,
  EstadoSimulacao,
  EstadoSimulacaoAdmin,
  IntencaoDeAnexo,
  MotivoDeEncerramentoPeloWeb,
  PaginaDeEventos,
  PaginaDeExecucoes,
  PaginaDePassos,
  RespostaDeInteracao,
  ResultadoDaAssinatura,
  ResultadoDeComando,
  ResultadoDoAvanco,
  SuperficieDaSimulacao,
} from '../types';

const BASE_DO_ESCRITORIO = '/negociacao/simulacoes';
/** O `requestBuilder` não manda `X-Workspace-Id` para `/admin*`: o escritório vai na query ou no corpo. */
const BASE_DO_ADMIN = '/admin/negociacao/simulacoes';

const LIMITE_DE_EVENTOS_POR_PAGINA = 100;
const LIMITE_DE_EXECUCOES_POR_PAGINA = 20;
const PASSOS_POR_PAGINA = 50;

/**
 * O 403 das rotas do cliente é a falta de EDITOR em NEGOCIACAO, e todas exigem `X-Workspace-Id`. A
 * recuperação de workspace do `requestBuilder` refaria a chamada sem o header (400
 * WORKSPACE_REQUIRED no lugar do 403) e poderia recarregar a página: por isso sai desligada. Nas
 * do admin o 403 é a falta de ADMIN, e não há workspace a recuperar.
 */
const chamar = (
  endpoint: string,
  opcoes: {
    method?: 'GET' | 'POST';
    body?: object;
    params?: Record<string, number | string>;
    chave?: string;
  } = {},
) =>
  request({
    endpoint,
    method: opcoes.method ?? 'GET',
    body: opcoes.body,
    params: opcoes.params,
    headers: opcoes.chave ? { 'Idempotency-Key': opcoes.chave } : undefined,
    recuperarWorkspaceNo403: false,
  });

const MENSAGEM_POR_CODIGO: Record<string, string> = {
  AGENTE_INDISPONIVEL: 'Este agente não está disponível para teste. A lista foi atualizada.',
  AGENTE_NAO_ENCONTRADO: 'O agente não foi encontrado. A lista foi atualizada.',
  PARAMETROS_INVALIDOS: 'Confira os dados para iniciar a simulação.',
  SIMULACAO_NAO_ENCONTRADA: 'Esta simulação não está mais disponível. Inicie uma nova.',
  MODO_INDISPONIVEL:
    'Este modo não está disponível para o escritório escolhido. A configuração foi recarregada.',
  REVISAO_DO_RASCUNHO_ALTERADA:
    'O rascunho foi salvo de novo depois que esta tela carregou. A configuração foi recarregada: confira os dados e inicie de novo.',
  PUBLICACAO_ALTERADA:
    'Uma nova versão foi publicada depois que esta tela carregou. A configuração foi recarregada: confira os dados e inicie de novo.',
  CONFIGURACAO_ALTERADA:
    'A configuração foi salva depois do início do teste. Reinicie para testar a configuração atual.',
  ESCRITORIO_NAO_ENCONTRADO: 'O escritório escolhido não foi encontrado ou está inativo.',
  EXECUCAO_NAO_ENCONTRADA: 'Esta execução não está mais disponível nesta simulação.',
  RODADA_EM_CURSO: 'A Lexi ainda está processando a mensagem anterior. Tente novamente.',
  PROCESSAMENTO_FALHOU: 'Não foi possível processar a mensagem. Tente novamente.',
  COMANDO_NAO_FALHOU: 'Esta mensagem já foi processada.',
  INTERACAO_FORA_DO_ESTADO: 'Esta opção não está mais disponível nesta conversa.',
  INTERACAO_JA_RESPONDIDA: 'Esta pergunta já foi respondida.',
  OPCAO_INVALIDA: 'Esta opção não é válida.',
  AVANCO_INVALIDO: 'Informe um número inteiro maior que zero, até 30 dias por avanço.',
  ANEXO_INVALIDO: 'O arquivo foi recusado. Confira o tipo e o tamanho e tente de novo.',
  ANEXO_NAO_PERMITIDO: 'O agente não está esperando um arquivo neste momento.',
  ARQUIVO_NAO_ENCONTRADO: 'Arquivo indisponível.',
  SIMULACAO_ENCERRADA: 'Simulação encerrada. Reinicie para conversar de novo.',
  ENTRADA_INVALIDA: 'Confira o que foi enviado e tente de novo.',
  COMANDO_NAO_ENCONTRADO: 'Esta mensagem não foi encontrada. Envie de novo.',
  COMANDO_INTERROMPIDO: 'O processamento da mensagem foi interrompido. Tente novamente.',
  ASSINATURA_NAO_ENCONTRADA: 'Este link de assinatura não está disponível.',
  ASSINATURA_JA_DECIDIDA: 'Esta assinatura de teste já tem um resultado registrado.',
};
const MENSAGEM_POR_STATUS: Record<number, string> = {
  403: 'Você não tem permissão para testar agentes neste escritório.',
  404: 'Esta simulação não está mais disponível. Inicie uma nova.',
};
const MENSAGEM_GENERICA = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * O texto de FRANQUIA_INDISPONIVEL (saldo, CA-34) é escrito para o usuário no core e muda com o
 * motivo (sem contrato, trial bloqueado, limite do trial). Os demais textos do core são de
 * diagnóstico e trazem ids: a tela usa o do código.
 */
const CODIGOS_COM_MENSAGEM_DO_CORE = new Set(['FRANQUIA_INDISPONIVEL']);

export class FalhaDaSimulacao extends Error {
  readonly erro: ErroDaSimulacao;

  constructor(erro: ErroDaSimulacao) {
    super(erro.mensagem);
    this.name = 'FalhaDaSimulacao';
    this.erro = erro;
  }
}

const detalhesDoErro = (corpo: unknown): Record<string, unknown> =>
  isRecord(corpo) && isRecord(corpo.error) && isRecord(corpo.error.details)
    ? corpo.error.details
    : {};

const camposInvalidos = (detalhes: Record<string, unknown>): string[] => {
  const { campos } = detalhes;
  if (Array.isArray(campos)) return campos.filter((campo) => typeof campo === 'string');
  return isRecord(campos) ? Object.keys(campos) : [];
};

const mensagemDaFalha = (corpo: unknown, status: number, codigo: string | null): string => {
  if (codigo && CODIGOS_COM_MENSAGEM_DO_CORE.has(codigo)) {
    return getApiErrorMessage(corpo, MENSAGEM_GENERICA);
  }
  return (
    (codigo ? MENSAGEM_POR_CODIGO[codigo] : undefined) ??
    MENSAGEM_POR_STATUS[status] ??
    MENSAGEM_GENERICA
  );
};

async function lerFalha(response: Response): Promise<FalhaDaSimulacao> {
  const corpo: unknown = await response.json().catch(() => null);
  const codigo = getApiErrorCode(corpo);
  const detalhes = detalhesDoErro(corpo);
  return new FalhaDaSimulacao({
    status: response.status,
    codigo,
    mensagem: mensagemDaFalha(corpo, response.status, codigo),
    camposInvalidos: camposInvalidos(detalhes),
    comandoId: typeof detalhes.comandoId === 'string' ? detalhes.comandoId : null,
  });
}

async function lerResposta<T>(response: Response): Promise<T> {
  if (!response.ok) throw await lerFalha(response);
  return parseApiResponse<T>(response);
}

/** No admin não há lista de agentes para recarregar: o texto do cliente não serve. */
const MENSAGEM_DO_ADMIN_POR_CODIGO: Record<string, string> = {
  AGENTE_NAO_ENCONTRADO: 'O agente deste fluxo não foi encontrado.',
  AGENTE_INDISPONIVEL: 'Este agente não está disponível para teste no escritório escolhido.',
};

async function lerRespostaDoAdmin<T>(response: Response): Promise<T> {
  if (response.ok) return parseApiResponse<T>(response);
  const falha = await lerFalha(response);
  const mensagem = falha.erro.codigo ? MENSAGEM_DO_ADMIN_POR_CODIGO[falha.erro.codigo] : undefined;
  throw mensagem ? new FalhaDaSimulacao({ ...falha.erro, mensagem }) : falha;
}

/**
 * O retry de um avanço ainda em curso volta 202 com `{comandoId, estado}`, sem `pendentes`: o que
 * sobrar aparece na próxima resposta, e o polling segue com o estado.
 */
async function lerAvanco(response: Response): Promise<ResultadoDoAvanco> {
  const resposta = await lerResposta<ResultadoDoAvanco | ResultadoDeComando>(response);
  return {
    estado: resposta.estado,
    pendentes:
      'pendentes' in resposta && typeof resposta.pendentes === 'number' ? resposta.pendentes : 0,
  };
}

/**
 * Os comandos de `/:id/*` são os mesmos nas duas superfícies; só a base muda. O escritório da
 * simulação do admin sai da linha no core, por isso nenhum deles leva escritório.
 */
const criarComandos = (base: string) => {
  const rotaDaSimulacao = (simulacaoId: string) => `${base}/${encodeURIComponent(simulacaoId)}`;
  const rotaDaAssinatura = (simulacaoId: string, assinaturaId: string) =>
    `${rotaDaSimulacao(simulacaoId)}/assinaturas/${encodeURIComponent(assinaturaId)}`;

  return {
    async encerrar(
      simulacaoId: string,
      motivo: MotivoDeEncerramentoPeloWeb,
      chave: string,
    ): Promise<EstadoSimulacao> {
      return lerResposta<EstadoSimulacao>(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/encerramento`, {
          method: 'POST',
          body: { motivo },
          chave,
        }),
      );
    },

    async enviarTexto(
      simulacaoId: string,
      texto: string,
      chave: string,
    ): Promise<ResultadoDeComando> {
      return lerResposta<ResultadoDeComando>(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/mensagens`, {
          method: 'POST',
          body: { texto },
          chave,
        }),
      );
    },

    async novaTentativa(
      simulacaoId: string,
      comandoId: string,
      chave: string,
    ): Promise<ResultadoDeComando> {
      return lerResposta<ResultadoDeComando>(
        await chamar(
          `${rotaDaSimulacao(simulacaoId)}/comandos/${encodeURIComponent(comandoId)}/nova-tentativa`,
          { method: 'POST', chave },
        ),
      );
    },

    async responderInteracao(
      simulacaoId: string,
      resposta: RespostaDeInteracao,
      chave: string,
    ): Promise<ResultadoDeComando> {
      return lerResposta<ResultadoDeComando>(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/interacoes`, {
          method: 'POST',
          body: resposta,
          chave,
        }),
      );
    },

    async avancarRelogio(
      simulacaoId: string,
      avanco: AvancoDoRelogio,
      chave: string,
    ): Promise<ResultadoDoAvanco> {
      return lerAvanco(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/relogio/avancos`, {
          method: 'POST',
          body: avanco,
          chave,
        }),
      );
    },

    /**
     * Continuação do avanço com `pendentes` > 0. Cada chamada é uma intenção com chave própria; só o
     * reenvio da mesma chamada repete a chave. O core processa sob CAS, então duas continuações não
     * disparam o mesmo vencimento.
     */
    async processarVencimentos(simulacaoId: string, chave: string): Promise<ResultadoDoAvanco> {
      return lerAvanco(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/vencimentos`, { method: 'POST', chave }),
      );
    },

    async buscarEventos(simulacaoId: string, aposSeq: number): Promise<PaginaDeEventos> {
      return lerResposta<PaginaDeEventos>(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/eventos`, {
          params: { aposSeq, limite: LIMITE_DE_EVENTOS_POR_PAGINA },
        }),
      );
    },

    async criarAnexo(
      simulacaoId: string,
      dados: DadosDoAnexo,
      chave: string,
    ): Promise<IntencaoDeAnexo> {
      return lerResposta<IntencaoDeAnexo>(
        await chamar(`${rotaDaSimulacao(simulacaoId)}/anexos`, {
          method: 'POST',
          body: dados,
          chave,
        }),
      );
    },

    /** Confirmar entrega o anexo ao flow, como uma mensagem do cliente: devolve o comando. */
    async confirmarAnexo(
      simulacaoId: string,
      anexoId: string,
      chave: string,
    ): Promise<ResultadoDeComando> {
      return lerResposta<ResultadoDeComando>(
        await chamar(
          `${rotaDaSimulacao(simulacaoId)}/anexos/${encodeURIComponent(anexoId)}/confirmacao`,
          { method: 'POST', chave },
        ),
      );
    },

    async acessarArquivo(simulacaoId: string, arquivoId: string): Promise<AcessoAoArquivo> {
      return lerResposta<AcessoAoArquivo>(
        await chamar(
          `${rotaDaSimulacao(simulacaoId)}/arquivos/${encodeURIComponent(arquivoId)}/acesso`,
        ),
      );
    },

    async buscarAssinatura(simulacaoId: string, assinaturaId: string): Promise<AssinaturaSimulada> {
      return lerResposta<AssinaturaSimulada>(
        await chamar(rotaDaAssinatura(simulacaoId, assinaturaId)),
      );
    },

    /**
     * O 200 traz a assinatura já decidida; o 202 do retry dentro do lease traz `{comandoId, estado}`.
     * Quem chama confere a forma e, sem a assinatura, relê pelo GET.
     */
    async registrarResultadoDaAssinatura(
      simulacaoId: string,
      assinaturaId: string,
      resultado: ResultadoDaAssinatura,
      chave: string,
    ): Promise<unknown> {
      return lerResposta<unknown>(
        await chamar(`${rotaDaAssinatura(simulacaoId, assinaturaId)}/resultado`, {
          method: 'POST',
          body: { resultado },
          chave,
        }),
      );
    },
  };
};

const comandosDoEscritorio = criarComandos(BASE_DO_ESCRITORIO);
const comandosDoAdmin = criarComandos(BASE_DO_ADMIN);

export const simulacaoService = {
  ...comandosDoEscritorio,

  async buscarAcesso(): Promise<AcessoDaSimulacao> {
    return lerResposta<AcessoDaSimulacao>(await chamar(`${BASE_DO_ESCRITORIO}/acesso`));
  },

  async iniciar(dados: DadosDeInicio, chave: string): Promise<EstadoSimulacao> {
    return lerResposta<EstadoSimulacao>(
      await chamar(BASE_DO_ESCRITORIO, { method: 'POST', body: dados, chave }),
    );
  },

  /**
   * Sem simulação atual, o core responde 404: para a tela é "nenhuma", não erro. Sem `return` de
   * promessa dentro do `async`: o polling lê a atual e os eventos em paralelo, e a atual precisa
   * chegar antes para a leitura final do encerramento sair no mesmo ciclo.
   */
  async buscarAtual(): Promise<EstadoSimulacao | null> {
    const response = await chamar(`${BASE_DO_ESCRITORIO}/atual`);
    if (response.status === 404) return null;
    return (await lerResposta<EstadoSimulacao | null>(response)) ?? null;
  },
};

export const simulacaoAdminService = {
  ...comandosDoAdmin,

  async buscarConfiguracao(flowId: string, escritorioId: string): Promise<ConfiguracaoDoAdmin> {
    return lerRespostaDoAdmin<ConfiguracaoDoAdmin>(
      await chamar(`${BASE_DO_ADMIN}/configuracao`, { params: { flowId, escritorioId } }),
    );
  },

  async iniciar(dados: DadosDeInicioDoAdmin, chave: string): Promise<EstadoSimulacaoAdmin> {
    return lerRespostaDoAdmin<EstadoSimulacaoAdmin>(
      await chamar(BASE_DO_ADMIN, { method: 'POST', body: dados, chave }),
    );
  },

  /**
   * A atual do usuário na superfície ADMIN deste escritório, de qualquer fluxo. O `/atual` do
   * admin tem dois 404: "nenhuma" (SIMULACAO_NAO_ENCONTRADA) e o escritório inativo ou de outra
   * empresa (ESCRITORIO_NAO_ENCONTRADO), que ler como "nenhuma" esconderia a falha.
   */
  async buscarAtual(escritorioId: string): Promise<EstadoSimulacaoAdmin | null> {
    const response = await chamar(`${BASE_DO_ADMIN}/atual`, { params: { escritorioId } });
    if (response.ok) return (await parseApiResponse<EstadoSimulacaoAdmin | null>(response)) ?? null;
    const falha = await lerFalha(response);
    if (falha.erro.codigo === 'SIMULACAO_NAO_ENCONTRADA') return null;
    throw falha;
  },

  async buscarExecucoes(simulacaoId: string, cursor: string | null): Promise<PaginaDeExecucoes> {
    return lerResposta<PaginaDeExecucoes>(
      await chamar(`${BASE_DO_ADMIN}/${encodeURIComponent(simulacaoId)}/execucoes`, {
        params: cursor
          ? { cursor, limite: LIMITE_DE_EXECUCOES_POR_PAGINA }
          : { limite: LIMITE_DE_EXECUCOES_POR_PAGINA },
      }),
    );
  },

  async buscarPassos(
    simulacaoId: string,
    execucaoId: string,
    page: number,
  ): Promise<PaginaDePassos> {
    return lerResposta<PaginaDePassos>(
      await chamar(
        `${BASE_DO_ADMIN}/${encodeURIComponent(simulacaoId)}/execucoes/${encodeURIComponent(execucaoId)}/passos`,
        { params: { page, pageSize: PASSOS_POR_PAGINA } },
      ),
    );
  },
};

/** Os comandos de uma simulação já criada, na base da superfície em que ela vive. */
export const comandosDaSuperficie = (superficie: SuperficieDaSimulacao) =>
  superficie === 'ADMIN' ? comandosDoAdmin : comandosDoEscritorio;
